import { useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { DeliveredMessages } from "../API";
import { useUser } from "../contexts/UserContext";
import { useDeliveredMessagesBySender } from "../graphql/hooks";
import { queryKeys } from "../graphql/queryKeys";
import { convertUtcToLocal, ensureHtmlBody } from "../utilities/utilities";
import MessageViewShell from "./MessageViewShell";
import BlockingPageLoader from "./ui/BlockingPageLoader";

const EMPTY_VALUE = "—";

function formatValue(value: string | number | null | undefined): string {
  if (typeof value === "number") {
    return value.toString();
  }

  const text = String(value ?? "").trim();
  return text.length > 0 ? text : EMPTY_VALUE;
}

function formatDateValue(value: string | null | undefined): string {
  const local = convertUtcToLocal(value ?? null, "NoDay");
  return local || EMPTY_VALUE;
}

function formatMessageRead(
  value: boolean | null | undefined,
  yes: string,
  no: string,
): string {
  if (value === true) return yes;
  if (value === false) return no;
  return EMPTY_VALUE;
}

function isOwnedDeliveredMessage(
  message: DeliveredMessages | null | undefined,
  userId: string,
  messageId: string,
): message is DeliveredMessages {
  return !!message && message.id === messageId && message.fromUserProfileId === userId;
}

function DeliveredMessageView() {
  const { user, isAuthenticated } = useUser();
  const userId = user?.userId ?? "";
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { t } = useTranslation("common");
  const { id: routeMessageId } = useParams<{ id?: string }>();

  const stateMessage = (location.state as DeliveredMessages | null) ?? null;
  const messageId = (routeMessageId ?? stateMessage?.id ?? "").trim();
  const deliveredQ = useDeliveredMessagesBySender(
    userId,
    isAuthenticated && !!userId,
  );

  const deliveredMessage = useMemo(() => {
    if (!messageId || !userId) return null;
    const cachedMessages =
      deliveredQ.data ??
      queryClient.getQueryData<DeliveredMessages[] | undefined>(
        queryKeys.deliveredMessagesBySender(userId),
      ) ??
      [];

    if (isOwnedDeliveredMessage(stateMessage, userId, messageId)) {
      return stateMessage;
    }

    return (
      cachedMessages.find((message) =>
        isOwnedDeliveredMessage(message, userId, messageId),
      ) ?? null
    );
  }, [deliveredQ.data, messageId, queryClient, stateMessage, userId]);

  const lastEditedLabel = useMemo(() => {
    const ts = deliveredMessage?.updatedAt ?? deliveredMessage?.createdAt ?? null;
    if (!ts) return null;

    try {
      return convertUtcToLocal(ts, "ShowDayLong");
    } catch {
      return ts;
    }
  }, [deliveredMessage]);

  const detailItems = useMemo(
    () =>
      deliveredMessage
        ? [
            {
              label: t("deliveredMessage.tableColumn.recipientEmail"),
              value: formatValue(deliveredMessage.recipientEmail),
            },
            {
              label: t("deliveredMessage.tableColumn.status"),
              value: formatValue(deliveredMessage.status),
            },
            {
              label: t("deliveredMessage.tableColumn.deliveredAt"),
              value: formatDateValue(deliveredMessage.deliveredAt),
            },
            {
              label: t("deliveredMessage.tableColumn.deliveryType"),
              value: formatValue(deliveredMessage.deliveryType),
            },
            {
              label: t("deliveredMessage.tableColumn.messageRead"),
              value: formatMessageRead(
                deliveredMessage.messageRead,
                t("common.yes"),
                t("common.no"),
              ),
            },
            {
              label: t("deliveredMessage.tableColumn.readReminderCount"),
              value: formatValue(deliveredMessage.readReminderCount),
            },
            {
              label: t("deliveredMessage.tableColumn.readReminderStatus"),
              value: formatValue(deliveredMessage.readReminderStatus),
            },
            {
              label: t("deliveredMessage.tableColumn.attemptCount"),
              value: formatValue(deliveredMessage.attemptCount),
            },
            {
              label: t("deliveredMessage.tableColumn.lastAttemptAt"),
              value: formatDateValue(deliveredMessage.lastAttemptAt),
            },
            {
              label: t("deliveredMessage.tableColumn.statusUpdatedAt"),
              value: formatDateValue(deliveredMessage.statusUpdatedAt),
            },
            {
              label: t("deliveredMessage.tableColumn.failureReason"),
              value: formatValue(deliveredMessage.failureReason),
            },
            {
              label: t("deliveredMessage.tableColumn.attachmentCount"),
              value: formatValue(deliveredMessage.attachmentCount),
            },
          ]
        : [],
    [deliveredMessage, t],
  );

  if (!isAuthenticated) {
    return (
      <div style={{ padding: "24px" }}>
        {t("deliveredMessage.onScreenMessage.unableToLoadMessage")}
      </div>
    );
  }

  if (!userId || (deliveredQ.isLoading && !deliveredMessage)) {
    return <BlockingPageLoader message={t("message.loadingData")} />;
  }

  if (!deliveredMessage) {
    return (
      <div style={{ padding: "24px" }}>
        {t("deliveredMessage.onScreenMessage.unableToLoadMessage")}
      </div>
    );
  }

  return (
    <MessageViewShell
      variant="delivered"
      title={formatValue(deliveredMessage.title)}
      lastEditedLabel={lastEditedLabel}
      deliveryLabel={t("deliveredMessage.tableColumn.deliveredAt")}
      deliveryValue={formatDateValue(deliveredMessage.deliveredAt)}
      bodyHtml={ensureHtmlBody(formatValue(deliveredMessage.bodySnippet))}
      bodyFooter={
        <div>
          <div className="msg-subtle">
            {t("deliveredMessage.onScreenMessage.snippetNotice")}
          </div>
          {deliveredMessage.originalMessageId ? (
            <button
              type="button"
              className="btn btn--white btn--size-md"
              onClick={() =>
                navigate(
                  `/LegacyMessageView/${deliveredMessage.originalMessageId}`,
                )
              }
            >
              {t("deliveredMessage.actionLabel.openInMessageView")}
            </button>
          ) : null}
        </div>
      }
      extraDetails={
        <section>
          <div className="msg-extra-details__title">
            {t("deliveredMessage.deliveryMetadata")}
          </div>
          <div className="msg-extra-details__grid">
            {detailItems.map((item) => (
              <div
                key={item.label}
                className="msg-extra-details__item"
              >
                <div className="msg-extra-details__label">{item.label}</div>
                <div className="msg-extra-details__value">{item.value}</div>
              </div>
            ))}
          </div>
        </section>
      }
      attachments={[]}
      showAttachmentsSection={false}
      onBack={() => navigate(-1)}
      onEdit={() => {}}
      editDisabled={true}
    />
  );
}

export default DeliveredMessageView;
